/**
 * <s-sticky-sidebar> keeps a desktop sidebar moving with the page. A short sidebar simply sticks
 * under the header. A sidebar taller than the screen scrolls along with the page until its bottom
 * comes into view, then stays; scrolling back up, it moves back down with the page until its top
 * is in view. So nothing is ever cut off, and it never jumps. Below 1024px it is a normal block.
 */
const DESKTOP = '(min-width: 1024px)';
const GAP = 16;

class StickySidebar extends HTMLElement {
  #ac;
  #top = 0;
  #lastY = 0;
  #ticking = false;
  #resize;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#lastY = window.scrollY;
    this.#top = this.#maxTop();

    const schedule = () => {
      if (this.#ticking) return;
      this.#ticking = true;
      requestAnimationFrame(() => {
        this.#ticking = false;
        this.#update();
      });
    };
    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { signal });
    this.#resize = new ResizeObserver(schedule);
    this.#resize.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resize?.disconnect();
  }

  /** Highest the sidebar sits: just under the header and the sticky filter bar. */
  #maxTop() {
    const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-toolbar-top')) || 0;
    return bar + GAP;
  }

  #update() {
    const y = window.scrollY;
    const delta = y - this.#lastY;
    this.#lastY = y;

    if (!matchMedia(DESKTOP).matches) {
      this.style.removeProperty('top');
      return;
    }

    const maxTop = this.#maxTop();
    // When taller than the screen, let it rise until its bottom sits GAP above the screen's bottom.
    const minTop = Math.min(maxTop, window.innerHeight - this.offsetHeight - GAP);
    this.#top = Math.min(maxTop, Math.max(minTop, this.#top - delta));
    this.style.top = `${Math.round(this.#top)}px`;
  }
}

if (!customElements.get('s-sticky-sidebar')) customElements.define('s-sticky-sidebar', StickySidebar);
